import {
    Colors,
    DATE_FORMATS,
    formatDate,
    Icon,
    ModalDialog,
    Pagination,
    Text,
} from '@amelio/ui-library';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QuestionAnswerCard } from './QuestionAnswerCard';
import { useProficiencyLevelPresentation } from './useProficiencyLevelPresentation';

import type { SkillItem } from '@/types/performance-profile';

import { useGetUserById } from '@/api/directory';
import { useGetAssessmentHistory } from '@/api/performance-profile';
import { resolveRankedProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { getLocalizedText } from '@/utils/localization';

export interface AssessmentHistoryModalRef {
    open: (skill: SkillItem) => void;
    close: () => void;
}

interface AssessmentHistoryModalProps {
    employeeId?: string;
    dataTestId?: string;
}

const PAGE_SIZE = 30;

export const AssessmentHistoryModal = forwardRef<
    AssessmentHistoryModalRef,
    AssessmentHistoryModalProps
>(({ employeeId, dataTestId = 'assessment-history-modal' }, ref) => {
    const { t, i18n } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const [skill, setSkill] = useState<SkillItem | null>(null);
    const [expandedEntryIds, setExpandedEntryIds] = useState<Set<string>>(new Set());

    const [pageNumber, setPageNumber] = useState(1);
    const { data: employeeUser } = useGetUserById({ userId: employeeId ?? '' });
    const { data, isLoading } = useGetAssessmentHistory(
        employeeId,
        skill?.id,
        pageNumber,
        PAGE_SIZE,
    );
    const { orderedLevels } = useProficiencyLevelPresentation();
    const history = data?.items ?? [];
    const totalCount = data?.totalCount ?? 0;
    const pageCount = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1;
    const resultStart = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1;
    const resultEnd = totalCount === 0 ? 0 : Math.min(pageNumber * PAGE_SIZE, totalCount);

    const toggleEntry = (entryId: string) => {
        setExpandedEntryIds((prev) => {
            const next = new Set(prev);
            if (next.has(entryId)) {
                next.delete(entryId);
            } else {
                next.add(entryId);
            }
            return next;
        });
    };

    const close = () => setIsOpen(false);

    useImperativeHandle(ref, () => ({
        open: (nextSkill: SkillItem) => {
            setSkill(nextSkill);
            setExpandedEntryIds(new Set());
            setPageNumber(1);
            setIsOpen(true);
        },
        close,
    }));

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={close}
            title={skill?.name ?? ''}
            titleIcon={
                <Icon
                    name={'time-clock'}
                    size={'md'}
                    color={Colors.SECONDARY_700}
                    ariaHidden
                />
            }
            subtitle={t('performanceProfile.skillsetAssessment.assessmentHistoryModal.subtitle')}
            size={'md'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={dataTestId}
            footer={
                <Pagination
                    currentPage={pageNumber}
                    totalPages={pageCount}
                    totalCount={totalCount}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPageNumber}
                    isLoading={isLoading}
                    dataTestId={`${dataTestId}-pagination`}
                    resultsSummaryText={t('common.showingResults', {
                        start: resultStart,
                        end: resultEnd,
                        total: totalCount,
                    })}
                />
            }
        >
            {isLoading && (
                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                    data-testid={`${dataTestId}-loading`}
                >
                    {t('performanceProfile.loading')}
                </Text>
            )}

            {!isLoading && history.length === 0 && (
                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                    className={'italic'}
                    data-testid={`${dataTestId}-empty`}
                >
                    {t('performanceProfile.skillsetAssessment.assessmentHistoryModal.empty')}
                </Text>
            )}

            {!isLoading &&
                history.map((entry, index) => {
                    const iconUrl = entry.proficiencyLevelIconUrl;
                    const iconName = entry.proficiencyLevelIconName;
                    const iconColor = entry.proficiencyLevelIconColor;
                    // Only the icon takes the level's own colour; the label and the timeline dot
                    // stay as they were.
                    const levelColor = resolveRankedProficiencyLevelColor({
                        level: { id: entry.proficiencyLevelId, iconColor },
                        orderedLevelIds: orderedLevels,
                    });

                    // Check if iconUrl is a custom URL (starts with http, https, /, or data:)
                    const isCustomUrl = iconUrl && /^https?:|^\/|^data:/.test(iconUrl);

                    let iconElement;
                    if (isCustomUrl) {
                        iconElement = (
                            <img
                                src={iconUrl}
                                alt={
                                    entry.proficiencyLevelLabel
                                        ? getLocalizedText(entry.proficiencyLevelLabel)
                                        : entry.proficiencyLevelId
                                }
                                className={'h-4 w-4'}
                                style={{ color: levelColor }}
                                data-testid={`${dataTestId}-entry-${entry.id}-icon`}
                            />
                        );
                    } else {
                        const fallbackIconName = iconName || 'time-clock';
                        const iconEntry = fallbackIconName ? getIconEntry(fallbackIconName) : null;
                        if (iconEntry) {
                            iconElement = (
                                <svg
                                    className={'h-4 w-4'}
                                    style={{ color: levelColor }}
                                    data-testid={`${dataTestId}-entry-${entry.id}-icon`}
                                    viewBox={'0 0 24 24'}
                                    stroke={'currentColor'}
                                    fill={'none'}
                                    strokeLinecap={'round'}
                                    strokeLinejoin={'round'}
                                    strokeWidth={2}
                                >
                                    {iconEntry.paths}
                                </svg>
                            );
                        } else {
                            iconElement = null;
                        }
                    }
                    return (
                        <div
                            key={entry.id}
                            className={'relative flex gap-3 pb-5 last:pb-0'}
                            data-testid={`${dataTestId}-entry-${entry.id}`}
                        >
                            {index !== history.length && (
                                <span
                                    className={
                                        'bg-border-subtle absolute top-5 left-3 h-full w-0.5 -translate-x-1/2'
                                    }
                                />
                            )}
                            <span
                                className={
                                    'relative z-10 mt-1 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white'
                                }
                                style={{ borderColor: iconColor || Colors.BLACK }}
                                data-testid={`${dataTestId}-entry-${entry.id}-dot`}
                            >
                                <span
                                    className={'h-2 w-2 rounded-full'}
                                    style={{ backgroundColor: iconColor || Colors.BLACK }}
                                    data-testid={`${dataTestId}-entry-${entry.id}-dot-center`}
                                ></span>
                            </span>
                            <div className={'flex-1 rounded-2xl bg-slate-100 p-4'}>
                                <div className={'flex flex-wrap items-center gap-2'}>
                                    <span className={'flex items-center gap-1'}>
                                        {iconElement}
                                        <Text
                                            weight={'semibold'}
                                            color={Colors.NAVY}
                                            className={'ml-2'}
                                        >
                                            {entry.proficiencyLevelLabel
                                                ? getLocalizedText(entry.proficiencyLevelLabel)
                                                : entry.proficiencyLevelId}
                                        </Text>
                                    </span>
                                </div>

                                <div className={'mt-3 mb-3 flex flex-wrap items-center gap-4'}>
                                    {entry.createdByName && (
                                        <span className={'flex items-center gap-1'}>
                                            <Icon
                                                name={'users-user'}
                                                size={'xs'}
                                                color={Colors.BASE_400}
                                            />
                                            <Text
                                                size={'small'}
                                                color={Colors.BASE_400}
                                                className={'italic'}
                                            >
                                                {entry.createdByName}
                                            </Text>
                                        </span>
                                    )}
                                    <span className={'flex items-center gap-1'}>
                                        <Icon
                                            name={'time-calendar'}
                                            size={'sm'}
                                            colorClassName={'text-gray-400'}
                                        />
                                        <Text
                                            size={'small'}
                                            color={Colors.BASE_400}
                                        >
                                            {formatDate({
                                                date: entry.createdAt,
                                                format: DATE_FORMATS.DISPLAY_DATE,
                                                language: i18n.language,
                                            })}
                                        </Text>
                                    </span>
                                    {entry.updatedFrom === 'assessment' && (
                                        <span
                                            className={
                                                'ml-auto rounded-lg bg-purple-50 px-2 py-0.5'
                                            }
                                            data-testid={`${dataTestId}-entry-${entry.id}-assessment-tag`}
                                        >
                                            <Text
                                                size={'small'}
                                                weight={'medium'}
                                                color={Colors.SECONDARY_700}
                                            >
                                                {t(
                                                    'performanceProfile.skillsetAssessment.assessmentHistoryModal.sourceAssessment',
                                                )}
                                            </Text>
                                        </span>
                                    )}
                                </div>

                                {entry.comment && (
                                    <div className={'border-base-100 mt-3 border-t pt-3'}>
                                        <Text
                                            size={'small'}
                                            color={Colors.BASE_500}
                                        >
                                            {entry.comment}
                                        </Text>
                                    </div>
                                )}

                                {entry.totalQuestionCount > 0 && (
                                    <div className={'mt-2'}>
                                        <button
                                            type={'button'}
                                            onClick={() => toggleEntry(entry.id)}
                                            data-testid={`${dataTestId}-entry-${entry.id}-toggle`}
                                            className={'flex items-center gap-1'}
                                        >
                                            <Icon
                                                name={'arrow-arrow-down'}
                                                size={'sm'}
                                                color={Colors.SECONDARY_700}
                                                rotate={expandedEntryIds.has(entry.id) ? 180 : 0}
                                            />
                                            <Text
                                                size={'small'}
                                                weight={'medium'}
                                                color={Colors.SECONDARY_700}
                                            >
                                                {expandedEntryIds.has(entry.id)
                                                    ? t(
                                                          'performanceProfile.skillsetAssessment.assessmentHistoryModal.hideQuestions',
                                                          {
                                                              count: entry.totalQuestionCount,
                                                              answered: entry.answeredQuestionCount,
                                                          },
                                                      )
                                                    : t(
                                                          'performanceProfile.skillsetAssessment.assessmentHistoryModal.viewQuestions',
                                                          {
                                                              count: entry.totalQuestionCount,
                                                              answered: entry.answeredQuestionCount,
                                                          },
                                                      )}
                                            </Text>
                                        </button>

                                        {expandedEntryIds.has(entry.id) && (
                                            <div
                                                className={'mt-3 flex flex-col gap-3'}
                                                data-testid={`${dataTestId}-entry-${entry.id}-questions`}
                                            >
                                                {(entry.questions ?? []).map((question) => (
                                                    <QuestionAnswerCard
                                                        key={question.questionId}
                                                        question={question}
                                                        employeeFirstName={employeeUser?.firstName}
                                                        dataTestId={`${dataTestId}-entry-${entry.id}-question-${question.questionId}`}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
        </ModalDialog>
    );
});

AssessmentHistoryModal.displayName = 'AssessmentHistoryModal';
